// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useMemo, useCallback, useEffect } from 'react';
import feedAPI from '../../api/feed';
import { FileUploadForm } from './FileUploadForm';
import { FileListTable } from './FileListTable';
import { FeedReview } from './FeedReview';
import { validateFile } from '../../utils/files';
import { ACCEPT_MAP, MAX_FILE_SIZE_BYTES } from '../../utils/constants';
import { Upload, FolderOpen, ListChecks } from 'lucide-react';

const FILE_TYPES = ['csv', 'xls', 'pdf'];

const apiError = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.error || data?.detail) return data.error || data.detail;
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ');
  return err?.message || fallback;
};

export default function BudgetFeedIOHome() {
  // ── Upload state ───────────────────────────────────────────────
  const [fileType, setFileType] = useState('csv');
  const [file, setFile] = useState(null);
  const [bankName, setBankName] = useState('');
  const [banks, setBanks] = useState([]);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [filePassword, setFilePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // ── File list state (FeedFile objects, keyed by id) ────────────
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileListMsg, setFileListMsg] = useState('');

  // ── Review state ───────────────────────────────────────────────
  const [reviewFile, setReviewFile] = useState(null); // { id, file_name }
  const [activeTab, setActiveTab] = useState('upload');

  const tabs = [
    { id: 'upload', label: 'Upload', icon: Upload },
    { id: 'filelist', label: 'Files', icon: FolderOpen },
    ...(reviewFile ? [{ id: 'review', label: 'Review', icon: ListChecks }] : []),
  ];

  const allowedMimes = useMemo(() => ACCEPT_MAP[fileType].mimes, [fileType]);

  useEffect(() => {
    feedAPI
      .banks()
      .then((res) => setBanks(res.data?.supported_banks ?? []))
      .catch(() => setBanks([]));
  }, []);

  // ── Fetch file list ────────────────────────────────────────────
  const fetchFiles = useCallback(async () => {
    setLoadingFiles(true);
    setFileListMsg('');
    try {
      const res = await feedAPI.listFiles();
      setFiles(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch (err) {
      setFileListMsg(`Error: ${apiError(err, 'Failed to load files.')}`);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'filelist') fetchFiles();
  };

  const openReview = (f) => {
    setReviewFile({ id: f.id, file_name: f.file_name });
    setActiveTab('review');
  };

  // ── File type toggle ───────────────────────────────────────────
  const handleTypeToggle = (type) => {
    if (type === fileType) return;
    setFileType(type);
    setFile(null);
    setMessage('');
    setIsEncrypted(false);
    setFilePassword('');
  };

  // ── File selection + validation ────────────────────────────────
  const handleFileChange = (selected) => {
    if (!selected) {
      setFile(null);
      return;
    }
    const error = validateFile(selected, fileType, allowedMimes, MAX_FILE_SIZE_BYTES);
    if (error) {
      setMessage(error);
      setFile(null);
      return;
    }
    setMessage('');
    setFile(selected);
  };

  // ── Upload → open review ───────────────────────────────────────
  const handleUpload = async () => {
    if (!file) {
      setMessage('Please select a file.');
      return;
    }
    if (isEncrypted && !filePassword) {
      setMessage('Please enter the file password.');
      return;
    }

    try {
      setUploading(true);
      setProgress(0);
      setMessage('');

      const res = await feedAPI.upload(
        {
          file,
          bankName: fileType === 'pdf' ? '' : bankName,
          password: fileType === 'pdf' && isEncrypted ? filePassword : '',
        },
        (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      );

      setMessage(`Uploaded successfully: ${res.data.message}`);
      setFile(null);
      setIsEncrypted(false);
      setFilePassword('');
      setProgress(100);
      openReview({ id: res.data.feed_file_id, file_name: res.data.file_name });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setMessage('Session expired. Please log in again.');
      else setMessage(`Error: ${apiError(err, 'Upload failed.')}`);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────
  const deleteFile = async (f) => {
    try {
      await feedAPI.deleteFile(f.id);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      setSelectedFiles((prev) => {
        const next = new Set(prev);
        next.delete(f.id);
        return next;
      });
      if (reviewFile?.id === f.id) setReviewFile(null);
      setFileListMsg(`${f.file_name} deleted.`);
    } catch (err) {
      setFileListMsg(`Error: ${apiError(err, 'Delete failed.')}`);
    }
  };

  const deleteBulk = async () => {
    if (selectedFiles.size === 0) return;
    const ids = Array.from(selectedFiles);
    const results = await Promise.allSettled(ids.map((id) => feedAPI.deleteFile(id)));
    const deleted = ids.filter((_, i) => results[i].status === 'fulfilled');
    setFiles((prev) => prev.filter((f) => !deleted.includes(f.id)));
    setSelectedFiles(new Set(ids.filter((id) => !deleted.includes(id))));
    if (deleted.includes(reviewFile?.id)) setReviewFile(null);
    setFileListMsg(
      deleted.length === ids.length
        ? `${deleted.length} file${deleted.length !== 1 ? 's' : ''} deleted.`
        : `Error: ${ids.length - deleted.length} file(s) could not be deleted.`,
    );
  };

  // ── Download original ──────────────────────────────────────────
  const downloadFile = async (f) => {
    try {
      const res = await feedAPI.downloadFile(f.id);
      const url = URL.createObjectURL(res.data);
      const link = Object.assign(document.createElement('a'), {
        href: url,
        download: f.file_name,
      });
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setFileListMsg(`Error downloading ${f.file_name}.`);
    }
  };

  // ── Selection helpers ──────────────────────────────────────────
  const toggleSelect = (id, checked) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedFiles(
      selectedFiles.size === files.length ? new Set() : new Set(files.map((f) => f.id)),
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">BudgetFeed.io</h1>
        <p className="text-gray-500 text-sm mt-1">
          Import bank statements, review the rows, then commit them as transactions.
        </p>
      </div>

      {/* Tab switcher */}
      <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1 gap-1">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => handleTabChange(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === id
                ? 'bg-white text-blue-700 shadow-sm border border-gray-200'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Upload panel */}
      {activeTab === 'upload' && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
          {/* File type toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            {FILE_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => handleTypeToggle(t)}
                className={`px-5 py-2 text-sm font-medium transition-colors ${
                  fileType === t ? 'bg-blue-700 text-white' : 'bg-white text-blue-700 hover:bg-blue-50'
                }`}
              >
                {ACCEPT_MAP[t].label}
              </button>
            ))}
          </div>

          <FileUploadForm
            fileType={fileType}
            file={file}
            onFileChange={handleFileChange}
            onUpload={handleUpload}
            uploading={uploading}
            message={message}
            progress={progress}
            isEncrypted={isEncrypted}
            onEncryptedChange={setIsEncrypted}
            password={filePassword}
            onPasswordChange={setFilePassword}
            showPassword={showPassword}
            onPasswordHoldStart={() => setShowPassword(true)}
            onPasswordHoldEnd={() => setShowPassword(false)}
            banks={banks}
            bankName={bankName}
            onBankChange={setBankName}
          />

          {/* Tips */}
          <ul className="text-xs text-gray-400 list-disc pl-4 space-y-1 pt-2 border-t border-gray-100">
            <li>CSV / Excel: the bank is detected from the headers; pick it if detection fails.</li>
            <li>PDF: upload readable, text-based PDFs — not scanned images.</li>
            <li>After upload you review every row before anything is saved as a transaction.</li>
            <li>Max file size is {MAX_FILE_SIZE_BYTES / 1024 / 1024}MB.</li>
          </ul>
        </div>
      )}

      {/* File list panel */}
      {activeTab === 'filelist' && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <FileListTable
            files={files}
            loading={loadingFiles}
            message={fileListMsg}
            selectedFiles={selectedFiles}
            onSelectAll={toggleSelectAll}
            onToggleSelect={toggleSelect}
            onReview={openReview}
            onDelete={deleteFile}
            onDownload={downloadFile}
            onRefresh={fetchFiles}
            onBulkDelete={deleteBulk}
          />
        </div>
      )}

      {/* Review panel */}
      {activeTab === 'review' && reviewFile && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <FeedReview
            key={reviewFile.id}
            fileId={reviewFile.id}
            fileName={reviewFile.file_name}
            onBack={() => handleTabChange('filelist')}
          />
        </div>
      )}
    </div>
  );
}
